import { useState, type ChangeEvent } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DocumentSchema,
  EmployeePictureTypeSchema,
  type DocumentDto,
  type DocumentTypeDto,
  type EmployeeDto,
} from '@taqon/contracts';
import { getData, newIdempotencyKey, postForm } from '../../shared/api/client';
import {
  DOCUMENTS,
  documentsKey,
  shortType,
  useRecordDocuments,
  useRecordDocumentTypes,
} from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { FileRules, oversizeMessage, useUploadLimit } from '../../shared/documents/upload-limit';
import { FormDialog } from '../../shared/ui/forms';
import { Section } from '../../shared/ui/page';
import { initials } from '../../shared/ui/person';
import { BRAND } from '../../shared/ui/tokens';

const EMPLOYEE = 'hr.employee';
const SIZE = { xs: 64, sm: 88 };

/**
 * What the record's picture is: the type named by `hr.employee_picture_document_type`, whether it
 * can hold a picture, and the newest available picture, if any.
 */
function usePicture(employee: EmployeeDto, canAttach: boolean) {
  const session = useSession();
  const canView = hasCapability(session.data, 'platform.document:view');
  const mayUpload = canAttach && hasCapability(session.data, 'platform.document:create');
  const pictureType = useQuery({
    queryKey: ['hr', 'employee', employee.id, 'picture-type'],
    enabled: canView,
    queryFn: () =>
      getData(`/api/v1/hr/employees/${employee.id}/picture-type`, EmployeePictureTypeSchema),
  });
  const documents = useRecordDocuments(EMPLOYEE, employee.id, canView);
  const types = useRecordDocumentTypes(EMPLOYEE, employee.id, canView);

  const ready = canView && pictureType.data !== undefined && types.data !== undefined;
  const code = pictureType.data?.documentTypeCode ?? null;
  const type = types.data?.find((t) => t.code === code);
  // The dialog asks for a file only, so a type that needs an expiry date cannot hold the picture.
  const usable = type !== undefined && !type.expiryTracked;
  const ofType = (documents.data ?? []).filter((d) => d.documentTypeCode === code);
  // The API lists the newest first.
  const picture = usable ? ofType.find((d) => d.status === 'available') : undefined;
  const scanning = usable && ofType.some((d) => d.status === 'pending_scan');
  return { ready, code, type, usable, picture, scanning, mayUpload };
}

/**
 * The address of the employee's current picture, where one is on file and the session may see it
 * (FRD-HRP-071); undefined otherwise. Same-origin, so it can be drawn into the business-card code.
 */
export function usePictureUrl(employee: EmployeeDto): string | undefined {
  const { picture } = usePicture(employee, false);
  return picture === undefined ? undefined : `${DOCUMENTS}/${picture.id}/content`;
}

/**
 * The employee's picture in the page banner (FRD-HRP-071): the picture where one is on file and
 * the session may see it, the initials otherwise. The picture opens large, as a rectangle, when
 * clicked (23 October 2026).
 */
export function EmployeeHeaderPicture({ employee }: { employee: EmployeeDto }) {
  const { picture } = usePicture(employee, false);
  const [open, setOpen] = useState(false);
  const src = picture === undefined ? undefined : `${DOCUMENTS}/${picture.id}/content`;
  const avatar = (
    <Avatar
      {...(src !== undefined ? { src, alt: `Picture of ${employee.name}` } : {})}
      sx={{
        flexShrink: 0,
        width: SIZE,
        height: SIZE,
        fontSize: { xs: 24, sm: 32 },
        fontWeight: 700,
        color: '#ffffff',
        bgcolor: 'rgba(255, 255, 255, 0.18)',
        border: '3px solid rgba(255, 255, 255, 0.85)',
        boxShadow: '0 10px 24px -12px rgba(0, 0, 0, 0.6)',
      }}
    >
      {initials(employee.name)}
    </Avatar>
  );
  if (src === undefined) return avatar;
  return (
    <>
      <ButtonBase
        aria-label={`Open the picture of ${employee.name}`}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(true);
        }}
        sx={{
          borderRadius: '50%',
          flexShrink: 0,
          transition: 'transform 150ms ease',
          '&:hover': { transform: 'scale(1.04)' },
          '&:focus-visible': { outline: '3px solid #ffffff', outlineOffset: 3 },
        }}
      >
        {avatar}
      </ButtonBase>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        maxWidth={false}
        aria-labelledby="employee-picture-title"
        slotProps={{
          paper: {
            sx: {
              overflow: 'hidden',
              m: { xs: 1, sm: 4 },
              maxWidth: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
              maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
            },
          },
        }}
      >
        <LargePicture src={src} employee={employee} />
        <Stack
          direction="row"
          spacing={2}
          sx={{ px: 2.5, py: 1.5, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography id="employee-picture-title" variant="subtitle1" sx={{ fontWeight: 700 }}>
            {employee.code} — {employee.name}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </Button>
        </Stack>
      </Dialog>
    </>
  );
}

/**
 * The picture as large as the screen allows in its own shape: scaled so that it fills the width
 * or the height, whichever comes first, with no band on either side. Until the picture has loaded
 * its shape is unknown, so it takes the largest square.
 */
function LargePicture({ src, employee }: { src: string; employee: EmployeeDto }) {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const narrow = window.innerWidth < 600;
  // The dialog's margins and the caption row below the picture.
  const maxW = Math.min(window.innerWidth - (narrow ? 16 : 64), 960);
  const maxH = window.innerHeight - (narrow ? 16 : 64) - 72;
  const box = Math.min(maxW, maxH);
  const scale = natural === null ? 0 : Math.min(maxW / natural.w, maxH / natural.h);
  const width = natural === null ? box : Math.round(natural.w * scale);
  const height = natural === null ? box : Math.round(natural.h * scale);
  return (
    <Box
      component="img"
      src={src}
      alt={`Picture of ${employee.name}`}
      onLoad={(event) => {
        const img = event.currentTarget;
        setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      }}
      sx={{ display: 'block', width, height, bgcolor: BRAND.charcoal, objectFit: 'contain' }}
    />
  );
}

/**
 * Adding or changing the employee's picture (FRD-HRP-071, CLR-39), on the employee record only.
 * The picture itself is shown in the page banner (`EmployeeHeaderPicture`), so this section appears
 * only to someone who may add one.
 *
 * The picture is an employee document of the type named by `hr.employee_picture_document_type`:
 * the newest available one is shown. Upload, virus scanning, access and audit are therefore the
 * document subsystem's (SDD §17), and who may add or replace it is whoever may attach documents to
 * the record — HR, never the employee through self-service (FRD-HRP-021). Without a usable type,
 * someone who could add a picture is told what is missing; everyone else sees nothing.
 */
export function EmployeePicture({
  employee,
  canAttach,
}: {
  employee: EmployeeDto;
  canAttach: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const { ready, code, type, usable, picture, scanning, mayUpload } = usePicture(
    employee,
    canAttach,
  );

  if (!ready || !mayUpload) return null;

  return (
    <Section title="Picture">
      <Stack direction="row" spacing={2.5} sx={{ p: 2, alignItems: 'center' }}>
        <Stack spacing={1} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
          {!usable && (
            <Alert severity="info" variant="outlined">
              {code === null
                ? 'No picture can be added yet: the setting hr.employee_picture_document_type is not set. An administrator sets it under Administration › Configuration.'
                : `No picture can be added yet: the document type "${code}" named by hr.employee_picture_document_type must be active, apply to employees and not track expiry.`}
            </Alert>
          )}
          {usable && !scanning && (
            <Typography variant="body2" color="text.secondary">
              {picture === undefined
                ? 'No picture on file.'
                : 'The picture on file is shown at the top of the page.'}
            </Typography>
          )}
          {scanning && (
            <Typography variant="body2" color="text.secondary" role="status">
              A new picture is being scanned and will appear at the top of the page shortly.
            </Typography>
          )}
          {usable && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddAPhotoIcon />}
              disabled={scanning}
              onClick={(event) => {
                event.currentTarget.focus();
                setUploading(true);
              }}
            >
              {picture === undefined ? 'Add picture' : 'Change picture'}
            </Button>
          )}
        </Stack>
      </Stack>
      {uploading && type !== undefined && (
        <PictureDialog
          employee={employee}
          type={type}
          current={picture ?? null}
          onClose={() => {
            setUploading(false);
          }}
        />
      )}
    </Section>
  );
}

/**
 * Sends the picture as a document of the picture type. Where the type allows replacement, the new
 * file supersedes the current picture once scanned and the earlier one is kept (SDD §17.3);
 * otherwise it is added as a further document and, being the newest, becomes the one shown.
 */
function PictureDialog({
  employee,
  type,
  current,
  onClose,
}: {
  employee: EmployeeDto;
  type: DocumentTypeDto;
  current: DocumentDto | null;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [file, setFile] = useState<File | null>(null);
  const limit = useUploadLimit();
  const replacing = current !== null && current.replacementAllowed ? current : null;

  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (replacing === null) {
        form.append('ownerRecordType', EMPLOYEE);
        form.append('ownerRecordId', employee.id);
      }
      form.append('documentTypeCode', type.code);
      form.append('title', `Picture of ${employee.name}`.slice(0, 200));
      // The file goes last: the API authorizes from the fields before it stores anything.
      if (file !== null) form.append('file', file, file.name);
      return postForm(
        replacing === null ? DOCUMENTS : `${DOCUMENTS}/${replacing.id}/replace`,
        form,
        DocumentSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: documentsKey(EMPLOYEE, employee.id) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      title={current === null ? 'Add a picture' : 'Change the picture'}
      submitLabel="Upload"
      canSubmit={file !== null && oversizeMessage(file, limit) === null}
      submitting={send.isPending}
      error={send.isError ? send.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        send.mutate();
      }}
    >
      <Alert severity="info" variant="outlined">
        The picture appears once it has been scanned.
        {current !== null && ' The earlier picture is kept with the employee documents.'}
      </Alert>
      <Box>
        <Typography component="label" htmlFor="employee-picture-file" variant="body2">
          Picture file *
        </Typography>
        <Box
          component="input"
          id="employee-picture-file"
          type="file"
          aria-label="Picture file"
          aria-required
          accept={type.allowedContentTypes.join(',')}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setFile(event.target.files?.[0] ?? null);
          }}
          sx={{ display: 'block', mt: 0.5, maxWidth: '100%' }}
        />
        <FileRules
          accepted={type.allowedContentTypes.map(shortType).join(', ')}
          file={file}
          limit={limit}
        />
      </Box>
    </FormDialog>
  );
}
